import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import type { FC, ReactNode } from "react";

import { cn } from "@/lib/utils";

import type { ChecklistRowDestination } from "./checklistRowLink";

export type ChecklistRowLink = ChecklistRowDestination & { disabled?: boolean };

export const CHECKLIST_ROW_GRID =
  "grid grid-cols-[auto_minmax(0,2fr)_minmax(max-content,1fr)] items-start gap-x-3 gap-y-0.5";

/** Vertical rhythm of a top-level checklist row. */
export const CHECKLIST_ROW_SPACING = "py-2.5 first:pt-1 last:pb-0";

/** The completion mark used across the app: green once done, faded while pending. */
export const ChecklistCheckIcon: FC<{ satisfied: boolean; className?: string }> = ({
  satisfied,
  className,
}) => (
  <CheckCircle2
    aria-hidden
    className={cn(
      "size-4 shrink-0",
      satisfied ? "text-success" : "text-muted-foreground opacity-50",
      className,
    )}
  />
);

/** Checklist row with a completion mark, title, and optional action and destination. */
export const ChecklistRow: FC<{
  title: ReactNode;
  detail?: string;
  satisfied: boolean;
  mark?: ReactNode;
  /** Resolves the row from here, e.g. the "complete item" button. */
  action?: ReactNode;
  link?: ChecklistRowLink | null;
  /** The link navigates away from the modal's own route — close it so it doesn't linger over the destination. */
  onNavigate?: () => void;
}> = ({ title, detail, satisfied, mark, action, link, onNavigate }) => (
  <li className={cn("flex gap-3", CHECKLIST_ROW_SPACING)}>
    <span className="flex min-h-6 items-center">
      {mark ?? <ChecklistCheckIcon satisfied={satisfied} />}
    </span>
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex min-h-6 items-center gap-3">
        <span className="min-w-0 flex-1 text-xs">{title}</span>
        {action}
        {link ? <ChecklistRowDeepLink link={link} onNavigate={onNavigate} /> : null}
      </div>
      {detail ? <span className="truncate text-xs text-muted-foreground">{detail}</span> : null}
    </div>
  </li>
);

export const ChecklistRowDeepLink: FC<{ link: ChecklistRowLink; onNavigate?: () => void }> = ({
  link,
  onNavigate,
}) => (
  <Link
    to={link.to}
    params={link.params}
    search={link.search}
    aria-disabled={link.disabled}
    onClick={(event) => {
      if (link.disabled) event.preventDefault();
      else onNavigate?.();
    }}
    className={cn(
      "inline-flex min-w-0 items-center gap-1 text-xs text-primary underline-offset-4 hover:underline",
      link.disabled && "pointer-events-none opacity-50",
    )}
  >
    <span className="truncate">{link.label}</span>
    <ArrowRight className="size-3 shrink-0" />
  </Link>
);
